import React, { useState, useRef, useCallback, useEffect, useMemo, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { useIntl, useSelector, useDispatch } from 'umi';
import { Justify, Button, Bubble } from 'tea-component';
import { throttle } from 'lodash-es';
import { useMemoizedFn } from 'ahooks';
import HomeBubble from '@home/components/TeaComponents/Bubble';
import SvgIcon from '@home/components/SvgIcon';
import { useLocal } from '@/pages/intelligence/hooks/customHook';
import useInput from './useInput';
import SavePopup from './SavePopup';
import StickyHeader from './StickyHeader';
import { buildStickySummaryItems } from './StickyHeader/summary';
import { useFilterSticky } from './hooks/useFilterSticky';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import '@/pages/intelligence/assets/scss/TopCharts/components/selectContext.scss';
import useSize from '@/pages/intelligence/hooks/useSize';
import useFilterSaveHook from '@/pages/intelligence/hooks/useFilterSaveHook';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import {
  DEFAULT_RELEASE_TIME_VALUE,
  toDateTypeNor,
  toDateTypeFilter,
  toDateTypeLy,
} from '@/pages/intelligence/utils/common';
import { getCountriesValues } from '@/pages/intelligence/pages/TopCharts/ConsoleGames/components/GamesDetails/StoreRank/utils';
import { STORERANK_METRICS_OPTIONS_LIST } from '@/pages/intelligence/_config/storeRank/config';
import {
  allMetricsOptions,
  getSourceMetricsByValue,
  getMetricOptionByValue,
  SEGMENT_DEFAULT_EXCLUDE_DATA_SOURCES,
  filterMetricsOptionsByDataSources,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import './index.scss';

/**
 * 用户手动改动某个筛选器时，用于判断"值是否真的发生变化"的签名函数表。
 *
 * 背景：valueRef.current 里的 prevValue 经常带 formatXxxPageFunc 产出的派生字段
 *      （如 genretor 上的 appmagic_genre / iegg_genre / genre_source），
 *      而 onChange 抛出来的 value 只有用户原始选择字段，两者直接 JSON.stringify
 *      对比必然不等，会把 hotTag 选中态误清。
 *
 * 这里只对"形状不是 { value: ... }"的特殊筛选器单独写签名；
 * 其余走默认 v?.value 对比，覆盖项目中绝大多数筛选器形状。
 */
const FILTER_VALUE_SIG: Record<string, (v: any) => string> = {
  [filterKeyMap.GENRETOR]: (v: any) =>
    JSON.stringify({
      source: v?.source,
      main: (v?.mainGenres || []).map((x: any) => x?.value),
      sub: (v?.subGenres || []).map((x: any) => x?.value),
      l3: (v?.level3Genres || []).map((x: any) => x?.value),
      l4: (v?.level4Genres || []).map((x: any) => x?.value),
      l5: (v?.level5Genres || []).map((x: any) => x?.value),
    }),
  [filterKeyMap.GENRETOR_WITH_SEGMENT]: (v: any) =>
    JSON.stringify({
      source: v?.source,
      main: (v?.mainGenres || []).map((x: any) => x?.value),
      sub: (v?.subGenres || []).map((x: any) => x?.value),
      l3: (v?.level3Genres || []).map((x: any) => x?.value),
      l4: (v?.level4Genres || []).map((x: any) => x?.value),
      l5: (v?.level5Genres || []).map((x: any) => x?.value),
    }),
};

const getFilterValueSig = (key: string, v: any): string => {
  const fn = FILTER_VALUE_SIG[key];
  if (fn) return fn(v);
  // 默认形状：{ value: ... }
  return JSON.stringify(v?.value);
};

/**
 * FilterBase组件实例
 */
export interface IFilterBaseInstance {
  /**
   * 设置筛选器value，并模拟触发点击筛选器组件Confirm按钮的行为
   * @param partialValue - 筛选器value（支持指定单个筛选器value）
   * @param options - 可选配置
   * @param options.resetToDefault - 是否先重置为默认值再合并，用于 HotTags 等需要清空其他筛选条件的场景
   */
  setValueAndConfirm: (partialValue: IRecord, options?: { resetToDefault?: boolean }) => void;
}

const FilterBase: React.ForwardRefRenderFunction<IFilterBaseInstance, any> = (props: any, ref) => {
  const { formatMessage } = useIntl();

  const {
    inputList = [], // 筛选器列表
    showButton = true, // 是否显示操作按钮
    resetType, // undefined,'loading','disabled'  暂时未用到
    defaultValue = {},
    value,
    onChange,
    onConfirm,
    onReset,
    iconText,
    buttonText,
    style,
    className,
    isHaveFilterSave,
    isCustomLayoutFilter = false,
    filterSaveType = '',
    onToggleMoreFilter,
    resetTip,
    exportTableCom,
    firstRowLeading,
    specialParams = {},
    enableSticky = false,
    stickyTopOffset = 0,
    stickyHeaderTitle,
    scrollContainer,
    filterGroups,
    /**
     * 点击 Confirm 按钮时，是否自动折叠筛选器面板（展开状态下）
     */
    useAutoCollapse = true,
  } = props;

  // console.debug('filterBase', props);
  const dispatch = useDispatch();
  const { filterData } = useFilterSaveHook({ filterSaveType });
  const { disabledDataSources = [] } = specialParams || {};
  /** 判断是否展示 'More Filter' */
  const divRef = useRef<HTMLElement | null>(null);
  const [summaryDisplayMap, setSummaryDisplayMap] = useState<Record<string, string>>({});
  /** 吸顶 wrapper ref（同时承担：阈值计算、滚动容器探测、ResizeObserver 测高）*/
  const filterWrapperRef = useRef<HTMLDivElement>(null);
  /** 吸顶展开态：点击摘要条后弹出完整面板 */
  const [stickyExpand, setStickyExpand] = useState(false);
  const [size] = useSize();
  const [showMoreText, setShowMoreText] = useState(false);
  const { getLocalText, locale } = useLocal();
  // const [isLockFilter, setLockFilter] = useState(false);
  const [visible, setVisible] = useState(false);
  // const [filterSaveType, setFilterSaveType] = useState('');
  const [isShow, setShow] = useState(false);
  // 重置中标志位，防止重置期间触发的 onChange 影响 dirty 状态
  const isResettingRef = useRef(false);
  // 是否有未保存的更改（dirty 状态）
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { isClickSensorTowTag, normalTimeFilterExtend, releaseTimeFilterExtend, isClickSteamOrGameModeTag } =
    useSelector((state: any) => state?.[FILTER_EXTEND_NAMESPACE] || {});

  const curValue = value || defaultValue || {};

  // 内部使用的value，无实际作用，只是用来触发组件更新
  const [internalValue, setInternalValue] = useState<any>(curValue);
  // 实际内部使用的value值
  const valueRef = useRef<any>(curValue);

  const handleCollapse = useMemoizedFn(() => {
    if (useAutoCollapse) {
      if (stickyExpand) {
        setStickyExpand(false);
      }
      if (isShow) {
        setShow(false);
        onToggleMoreFilter?.(false);
      }
    }
  });

  const handleClickComfirm = useCallback(
    throttle(
      () => {
        onConfirm?.({ ...defaultValue, ...valueRef.current });
        // 确认后重置 dirty 状态
        setHasUnsavedChanges(false);
        handleCollapse();
      },
      500,
      { leading: true },
    ),
    [showButton, onConfirm, handleCollapse],
  );

  const handleClickReset = useCallback(
    throttle(
      () => {
        if (!resetType) {
          const newValue = { ...defaultValue };
          // 设置重置中标志
          isResettingRef.current = true;
          // 重置值并设置 dirty 状态，允许用户用重置后的值确认
          setHasUnsavedChanges(true);
          // 更新 valueRef 和 internalValue
          valueRef.current = newValue;
          setInternalValue(newValue);
          // 在下一个事件循环清除标志
          setTimeout(() => {
            isResettingRef.current = false;
          }, 0);
          onReset?.(newValue);
        }
      },
      500,
      { leading: true },
    ),
    [showButton, onReset, resetType],
  );

  const onFilterChange = useCallback(
    (key: string, value: any, isClose?: Boolean) => {
      const normalizedIsClose = isClose?.valueOf();
      const newValue = {
        ...valueRef.current,
        [key]: value,
        isClose: normalizedIsClose,
      };

      const isNoButtonMyFilter = !showButton && key === 'myFilter';
      // console.log('onFilterChange-key', key);
      // console.log('onFilterChange-value', value);
      // console.log('onFilterChange-showButton', showButton);
      // console.log('onFilterChange-newValue', newValue);
      // console.log('valueRef.current', valueRef.current);
      // console.log('inputList--inputList', inputList);
      // console.log('defaultValue--defaultValue', defaultValue);
      let newValueTemp = newValue;

      // 如果点击重置按钮，则使用默认筛选器配置-kpitrends-reset
      if (key === 'KPITrends_reset') {
        newValueTemp = {
          ...newValueTemp,
          ...defaultValue,
          [filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]: {
            ...newValueTemp[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND],
          },
          [filterKeyMap.RANGE_TIME]: {
            type: newValueTemp[filterKeyMap.RANGE_TIME]?.type,
            value: ['', ''],
          },
          [filterKeyMap.PLATFORMCASCADER]: {
            type: newValueTemp[filterKeyMap.CASCADER]?.type,
            value: {
              mscience: ['PC', 'PlayStation', 'Xbox', 'Nintendo'],
              gsd: ['PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch'],
              vginsights: ['PC'],
              npd: ['PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch', 'WII U'],
              ampere: ['PC', 'PlayStation', 'Xbox'],
              newzoo: ['PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch'],
            },
          },
          [filterKeyMap.CASCADER]: {
            value: {
              mscience: [
                'Full Game',
                'MTX',
                'DLC: All Other',
                'DLC: Expansion',
                'DLC: Content Pre-Purchase Pass',
                'DLC: Unlockable Content Pass',
                'Subscription',
              ],
              gsd: ['Full Game', 'In-Game Currency'],
            },
          },
        };
      }

      // console.log('defaultValue--newValueTemp', newValueTemp);

      // 更改指标筛选器时，特殊化处理逻辑
      if (key === 'metric_selection_for_trends') {
        const newDateType = toDateTypeNor[value?.value?.type];
        if (newDateType && newDateType !== '' && newDateType !== toDateTypeNor[newValue?.rangeTime?.type]) {
          newValueTemp = {
            ...newValueTemp,
            rangeTime: {
              type: newDateType,
              value: ['', ''],
            },
          };
        }
      }
      if (key === 'align_to_release') {
        if (value?.value) {
          const newDateNormalTypeTemp = toDateTypeFilter[newValue?.rangeTime?.type];
          newValueTemp = {
            ...newValueTemp,
            rangeReleaseTime: {
              type: newDateNormalTypeTemp,
              value:
                releaseTimeFilterExtend[newDateNormalTypeTemp] &&
                releaseTimeFilterExtend[newDateNormalTypeTemp]?.length > 1 &&
                releaseTimeFilterExtend[newDateNormalTypeTemp]?.[0] !== ''
                  ? releaseTimeFilterExtend[newDateNormalTypeTemp]
                  : DEFAULT_RELEASE_TIME_VALUE[newDateNormalTypeTemp],
            },
          };
        } else {
          const newDateReleaseTypeTemp = toDateTypeFilter[newValue?.rangeReleaseTime?.type];
          newValueTemp = {
            ...newValueTemp,
            rangeTime: {
              type: newDateReleaseTypeTemp,
              value: normalTimeFilterExtend[newDateReleaseTypeTemp] || ['', ''],
            },
          };
        }
      }

      if (key === 'engageLevel_segement_twoB') {
        newValueTemp = {
          ...newValueTemp,
          [filterKeyMap.ENGAGELEVEL_SEGEMENT_TWO]: {
            value: {
              ...newValueTemp[filterKeyMap.ENGAGELEVEL_SEGEMENT_TWO]?.value,
              engagementLevelB: value?.value,
            },
          },
        };
      }
      if (key === 'engageLevel_segement_twoA') {
        newValueTemp = {
          ...newValueTemp,
          [filterKeyMap.ENGAGELEVEL_SEGEMENT_TWO]: {
            value: {
              ...newValueTemp[filterKeyMap.ENGAGELEVEL_SEGEMENT_TWO]?.value,
              engagementLevelA: value?.value,
            },
          },
        };
      }
      const isPlayed = newValueTemp?.overlapSteam_players?.value === 'played';
      if (key === 'revenueGrowthTwoA') {
        newValueTemp = {
          ...newValueTemp,
          [filterKeyMap.REVENUE_GROWTH_TWO]: {
            value: {
              ...newValueTemp[filterKeyMap.REVENUE_GROWTH_TWO]?.value,
              revenueGrowthPP: isPlayed
                ? value?.value
                : newValueTemp[filterKeyMap.REVENUE_GROWTH_TWO]?.value?.revenueGrowthPP,
              revenueGrowthWP: isPlayed
                ? newValueTemp[filterKeyMap.REVENUE_GROWTH_TWO]?.value?.revenueGrowthWP
                : value?.value,
            },
          },
        };
      }
      if (key === 'revenueGrowthTwoB') {
        newValueTemp = {
          ...newValueTemp,
          [filterKeyMap.REVENUE_GROWTH_TWO]: {
            value: {
              ...newValueTemp[filterKeyMap.REVENUE_GROWTH_TWO]?.value,
              revenueGrowthPW: isPlayed
                ? value?.value
                : newValueTemp[filterKeyMap.REVENUE_GROWTH_TWO]?.value?.revenueGrowthPW,
              revenueGrowthWW: isPlayed
                ? newValueTemp[filterKeyMap.REVENUE_GROWTH_TWO]?.value?.revenueGrowthWW
                : value?.value,
            },
          },
        };
      }

      if (key === 'overlapSteam_players') {
        newValueTemp = {
          ...newValueTemp,
          [filterKeyMap.REVENUE_GROWTH_TWO]: {
            value: {
              ...newValueTemp[filterKeyMap.REVENUE_GROWTH_TWO]?.value,
              segmentName: value?.value,
            },
          },
        };
      }

      // 切换storerank-detail-combine数据源时，特殊处理逻辑
      if (key === 'composed_select') {
        const composedSelectFilter = inputList?.find((item: any) => item.key === 'composed_select');
        const composedCountrySelectFilter = inputList?.find((item: any) => item.key === 'composed_country_select');
        // 若filterKeyType为composed_select_combine_detail_storerank，则是需要特殊处理筛选器
        if (composedSelectFilter?.filterKeyType === 'composed_select_combine_detail_storerank') {
          const regionsOptions = composedCountrySelectFilter?.regionsOptionsMap?.[value?.value?.parent];

          const regionsOptinosValues = getCountriesValues(regionsOptions);
          // const countriesOld =
          //   newValueTemp[filterKeyMap.COMPOSED_COUNTRY_SELECT]?.value?.countries || [];

          // 对国家进行取交集处理
          // const result =
          //   countriesOld?.length > 0
          //     ? formatRegionParams(
          //         newValueTemp[filterKeyMap.COMPOSED_COUNTRY_SELECT]?.value?.countries,
          //         regionsOptinosValues,
          //       )
          //     : regionsOptinosValues;

          newValueTemp = {
            ...newValueTemp,
            [filterKeyMap.COMPOSED_COUNTRY_SELECT]: {
              value: {
                ...newValueTemp[filterKeyMap.COMPOSED_COUNTRY_SELECT]?.value,
                countries: regionsOptinosValues,
              },
            },
          };

          if (
            valueRef.current?.[filterKeyMap.COMPOSED_SELECT]?.value?.parent !==
            newValueTemp[filterKeyMap.COMPOSED_SELECT]?.value?.parent
          ) {
            const metricObjList =
              STORERANK_METRICS_OPTIONS_LIST?.filter(
                (item: any) => item.dataSource === newValueTemp[filterKeyMap.COMPOSED_SELECT]?.value?.parent,
              ) || [];
            newValueTemp = {
              ...newValueTemp,
              [filterKeyMap.COMPOSED_SELECT]: {
                value: {
                  ...newValueTemp[filterKeyMap.COMPOSED_SELECT]?.value,
                  child: metricObjList?.length > 0 ? metricObjList[0]?.value : '',
                },
              },
            };
          }
        }
      }

      // 如果是操作的时间筛选器，且改变的是粒度，同时有指标筛选器，且此时使用了指标筛选器快捷键，默认赋值此快捷键对应所有指标
      if (
        key === 'rangeTime' &&
        valueRef.current?.[filterKeyMap.RANGE_TIME]?.type !== newValueTemp[filterKeyMap.RANGE_TIME]?.type
      ) {
        if (
          Object.prototype.hasOwnProperty.call(newValueTemp, 'metric_selection_for_trends') &&
          newValueTemp[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value?.shotSelectValue !== 'all'
        ) {
          let filterMetricsByDateType = allMetricsOptions.filter((element: any) =>
            element.dateEnum.includes(toDateTypeLy[newValueTemp[filterKeyMap.RANGE_TIME]?.type]),
          );
          const excludeDataSources = Array.from(new Set([...SEGMENT_DEFAULT_EXCLUDE_DATA_SOURCES, ...disabledDataSources]));
          filterMetricsByDateType = filterMetricsOptionsByDataSources(filterMetricsByDateType, excludeDataSources);
          const defaultMetricList: string[] = [];
          const keyEnums =
            newValueTemp[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value?.selectedMetricsValue === 'metrics'
              ? 'metricsType'
              : 'dataSource';
          filterMetricsByDateType.forEach((ele: any) => {
            if (ele[keyEnums] === newValueTemp[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value?.shotSelectValue) {
              defaultMetricList.push(ele.value);
            }
          });
          newValueTemp = {
            ...newValueTemp,
            [filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]: {
              value: {
                ...newValueTemp[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value,
                metrics: defaultMetricList,
                source_metrics: getSourceMetricsByValue(defaultMetricList),
                metricsOptions: getMetricOptionByValue(defaultMetricList),
                type: toDateTypeLy[newValueTemp[filterKeyMap.RANGE_TIME]?.type],
              },
            },
          };
        }
      }
      // 用户手动修改非 hotTag 筛选器时，仅当值真正发生变化才清空 hotTag 选中态。
      // 对比逻辑：默认看 v?.value 字段；形状特殊的筛选器（如 genretor）在 FILTER_VALUE_SIG 里登记。
      // 这样 prevValue 上的派生字段 / 缓存 options 都不会引入误判。
      if (
        key !== filterKeyMap.HOT_TAGS_SOURCE &&
        newValueTemp[filterKeyMap.HOT_TAGS_SOURCE]?.value
      ) {
        const prevValue = valueRef.current?.[key];
        const isValueChanged = getFilterValueSig(key, prevValue) !== getFilterValueSig(key, value);
        if (isValueChanged) {
          newValueTemp[filterKeyMap.HOT_TAGS_SOURCE] = {};
        }
      }

      onChange?.(key, value, newValueTemp, isNoButtonMyFilter);

      // 更新value
      valueRef.current = newValueTemp;
      // 触发组件更新
      setInternalValue(newValueTemp);

      // 筛选器值改变，标记为 dirty
      if (!isResettingRef.current) {
        setHasUnsavedChanges(true);
      }

      // 如果没有操作按钮，直接触发confirm
      // 如果没有操作按钮，且点击的是myFilter，需要先获取筛选器的参数，再同时调接口与渲染筛选器的值
      if (!showButton && key !== 'myFilter') {
        // console.log('onFilterChange-onConfirm!!~!~~~~~', newValueTemp);
        onConfirm?.(newValueTemp, isCustomLayoutFilter);
      }
      // console.log(valueRef.current);
    },
    [onConfirm, showButton, onChange, disabledDataSources],
  );

  const handleSummaryDisplayChange = useCallback((key: string, display: string): void => {
    setSummaryDisplayMap((prev) => {
      if (prev[key] === display) return prev;
      return {
        ...prev,
        [key]: display,
      };
    });
  }, []);

  // 更新外部的value值到内部
  useMemo(() => {
    // console.log('JSON.stringify(value)', value);
    if (value) {
      valueRef.current = value;
    }
  }, [JSON.stringify(value)]);

  const { renderInput } = useInput({
    value,
    defaultValue,
    internalValue,
    inputList,
    curValue: valueRef.current,
    locale,
    isCustomLayoutFilter,
    getLocalText,
    onFilterChange,
    setVisible,
    filterData,
    filterSaveType,
    exportTableCom,
    firstRowLeading,
    specialParams,
    filterGroups,
    onSummaryDisplayChange: handleSummaryDisplayChange,
  });

  /**
   * 从外部设置筛选器的值并触发确认
   * @param partialValue 要更新的筛选器值（部分或全部）
   * @param options 可选配置
   * @param options.resetToDefault 是否先重置为默认值再合并，用于 HotTags 等需要清空其他筛选条件的场景
   */
  const setValueAndConfirm = (partialValue: IRecord, options?: { resetToDefault?: boolean }) => {
    const genreSelectKey = filterKeyMap.GENRETOR;
    // 当 resetToDefault 为 true 时，以 defaultValue 为基础值，确保未指定的筛选条件被重置为默认
    // 否则保持原有行为，基于当前筛选器的实际值 valueRef.current 进行合并
    const baseValue = options?.resetToDefault ? defaultValue : valueRef.current;
    const newValue = {
      ...baseValue,
      ...partialValue,
      // 特殊处理品类筛选器value
      [genreSelectKey]: {
        ...baseValue?.[genreSelectKey],
        ...partialValue?.[genreSelectKey],
      },
      // resetToDefault 时保留当前 hotTagsSource 的选中值（HotTag 选中后先 onChange 再 setValueAndConfirm）
      ...(options?.resetToDefault && valueRef.current?.[filterKeyMap.HOT_TAGS_SOURCE]
        ? { [filterKeyMap.HOT_TAGS_SOURCE]: valueRef.current[filterKeyMap.HOT_TAGS_SOURCE] }
        : {}),
    };

    // resetToDefault 时，遍历 inputList 中带 ref 的筛选器组件，调用其 reset() 清空内部状态
    // 如 Company 筛选器的搜索关键词/分页、SteamTags 的选中态等
    // 但跳过 partialValue 中已有新值的筛选器，避免 HotTag 解析出的筛选器值被 reset 覆盖
    if (options?.resetToDefault) {
      inputList.forEach((item: any) => {
        if (item?.ref?.current?.reset instanceof Function) {
          // 如果 partialValue 中已包含该筛选器的值，则不 reset，保持 HotTag 等外部设置的值
          const itemKey = item?.key;
          if (itemKey && partialValue?.[itemKey] !== undefined) {
            return;
          }
          item.ref.current.reset();
        }
      });
    }

    valueRef.current = newValue;
    setInternalValue(newValue);
    // 设置后，标记为 dirty
    setHasUnsavedChanges(true);
    onConfirm?.(newValue);
  };

  useImperativeHandle(ref, () => ({
    setValueAndConfirm,
  }));

  /** 吸顶相关状态 (enableSticky=false 时 hook 提前返回) */
  const sticky = useFilterSticky({
    enabled: enableSticky,
    ref: filterWrapperRef,
    scrollContainer,
  });

  const stickySummaryItems = useMemo(() => {
    if (!enableSticky) return [];
    return buildStickySummaryItems({
      inputList,
      value: valueRef.current,
      formatMessage,
      locale,
      summaryDisplayMap,
    });
  }, [enableSticky, inputList, internalValue, value, formatMessage, locale, summaryDisplayMap]);

  const handleFilterContainerRef = useCallback((node: HTMLElement | null): void => {
    divRef.current = node;
  }, []);

  /** 判断是否展示 'More Filter' */
  useEffect(() => {
    const el = divRef?.current as HTMLElement | null;
    const nextShowMoreText = Boolean(el && el.scrollHeight > 60);
    const isHiddenStickyMeasurement =
      enableSticky &&
      sticky.filterFixed &&
      !nextShowMoreText &&
      (el?.scrollHeight ?? 0) === 0 &&
      (el?.offsetHeight ?? 0) === 0;
    if (isHiddenStickyMeasurement) {
      return;
    }
    if (nextShowMoreText) {
      // 筛选器高度大于一行
      setShowMoreText(true);
    } else {
      setShowMoreText(false);
    }
  }, [size, divRef?.current?.scrollHeight, sticky.filterFixed]);


  /** 详情点击sensorTower tag跳转榜单需展开More Filter */
  useEffect(() => {
    if ((isClickSensorTowTag || isClickSteamOrGameModeTag) && showMoreText) {
      setShow(true);

      isClickSensorTowTag &&
        dispatch({
          type: `${FILTER_EXTEND_NAMESPACE}/saveIsClickSensorTowTag`,
          payload: false,
        });

      isClickSteamOrGameModeTag &&
        dispatch({
          type: `${FILTER_EXTEND_NAMESPACE}/saveIsClickSteamOrGameModeTag`,
          payload: false,
        });
    }
  }, [isClickSensorTowTag, isClickSteamOrGameModeTag, showMoreText]);

  const filterBody = (
    <div
      style={style}
      className={classnames('dbi-searchbox', className, {
        'show-all': isShow || !showButton || (enableSticky && sticky.filterFixed && stickyExpand),
        'dbi-searchbox--grouped': filterGroups?.length > 0,
      })}
    >
      <div ref={handleFilterContainerRef} className={isCustomLayoutFilter ? 'dbi-custom-filter' : 'dbi-searchbox-flex'}>
        {renderInput()}
        {/* {
          haveFilterSaveTextBtn &&
          <Bubble
            dark
            content={filterData?.length >= 10 ? formatMessage({ id: 'dbi_filterSave_my_filter_top10' }) : ''}>
            <div
              onClick={() => {
                setVisible(true);
              }}
            >
              {formatMessage({ id: 'dbi_filterSave_filter_setting' })}
            </div>
          </Bubble>
        } */}
        {iconText && <TipContext AWidth={555} placement="left" tipText={iconText} className="tip-icon" />}
      </div>
      {showButton && (
        <Justify
          className={isShow ? 'tip-justify' : 'noshow-justify'}
          left={
            <>
              <HomeBubble
                className="dbi-bubble-max-width"
                content={hasUnsavedChanges ? undefined : formatMessage({ id: 'dbi_no_filter_changes' })}
              >
                <Button type="primary" disabled={!hasUnsavedChanges} onClick={handleClickComfirm}>
                  {formatMessage({ id: 'dbi_confirm' })}
                </Button>
              </HomeBubble>
              {resetTip ? (
                <Bubble content={resetTip} style={{ maxWidth: 400 }}>
                  <Button type="weak" onClick={handleClickReset}>
                    {formatMessage({ id: 'dbi_reset' })}
                  </Button>
                </Bubble>
              ) : (
                <Button type="weak" onClick={handleClickReset}>
                  {formatMessage({ id: 'dbi_reset' })}
                </Button>
              )}
              {isHaveFilterSave && (
                <Bubble
                  dark
                  content={filterData?.length >= 10 ? formatMessage({ id: 'dbi_filterSave_my_filter_top10' }) : ''}
                >
                  <Button
                    type="weak"
                    onClick={() => {
                      setVisible(true);
                    }}
                    disabled={filterData?.length >= 10}
                  >
                    {formatMessage({ id: 'dbi_filterSave_filter_setting' })}
                  </Button>
                </Bubble>
              )}
              {buttonText && <span className="dbi-searchbox-text">{buttonText}</span>}
            </>
          }
          right={
            (() => {
              if (!showMoreText) return null;

              if (enableSticky && sticky.filterFixed) {
                return (
                  <div
                    className="dbi-filterfixed-header__show dbi-filterfixed-header__show--close"
                    onClick={() => {
                      setStickyExpand(false);
                    }}
                  >
                    <span>{formatMessage({ id: 'dbi_close' })}</span>
                    <SvgIcon
                      type="point-top"
                      className="dbi-filterfixed-header__show-arrow"
                    />
                  </div>
                );
              }

              return (
                <Button
                  type="link"
                  className="dbi-searchbox-btn"
                  onClick={() => {
                    setShow(!isShow);
                    onToggleMoreFilter?.(!isShow);
                  }}
                >
                  {isShow ? formatMessage({ id: 'dbi_collapse_filter' }) : formatMessage({ id: 'dbi_more_filter' })}
                </Button>
              );
            })()
          }
        />
      )}
      {visible && (
        <SavePopup
          saveOpen={visible}
          close={() => {
            setVisible(!setVisible);
          }}
          inputList={inputList}
          internalValue={valueRef.current}
          filterSaveType={filterSaveType}
          handlePopupConfirm={(value: string) => {
            valueRef.current = {
              ...valueRef.current,
              myFilter: { value },
            };
          }}
        />
      )}
    </div>
  );

  // enableSticky=false 时直接返回原来的 DOM 结构
  if (!enableSticky) {
    return filterBody;
  }

  return (
    <div className="dbi-filterbox" style={{ minHeight: sticky.filterFixed ? sticky.filterHeight : undefined }}>
      <div
        ref={filterWrapperRef}
        className={classnames('dbi-filterfixed-wrapper', {
          'dbi-filterbox-fixed filter-visiable': sticky.filterFixed,
          'filter-expand': sticky.filterFixed && stickyExpand,
        })}
        style={sticky.filterFixed ? { top: stickyTopOffset } : undefined}
      >
        <StickyHeader
          summaryItems={stickySummaryItems}
          pageTitle={stickyHeaderTitle}
          onClick={() => setStickyExpand(true)}
        />

        <div className={sticky.filterFixed ? 'dbi-filterbox-header' : ''}>
          {filterBody}
        </div>

        <div className="dbi-filterbox-mask" onClick={() => setStickyExpand(false)} />
      </div>
    </div>
  );
};

export default React.forwardRef(FilterBase);
